import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal.tsx';
import { Avatar } from '../common/Avatar.tsx';
import { DescriptionEditor } from '../common/DescriptionEditor.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { TaskStatus, PriorityLevel, Project, User, ProjectBuild, ProjectFeature } from '../../../shared/types.ts';
import { FolderPlus, Plus, X, Loader2, CheckCircle2, Paperclip, UploadCloud, FileText, Layers, Package, Calendar, Eye, Users } from 'lucide-react';
import { DocumentPreviewModal } from '../common/DocumentPreviewModal.tsx';
import { ExistingProjectMembersModal } from '../projects/ExistingProjectMembersModal.tsx';
import { canCreateProject } from '../../../shared/projectPermissions.ts';
import { describeUploadFailure } from '../../services/uploadErrors.ts';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTaskCreated: (createdTask?: any) => void;
  projectId?: string;
  initialStatus?: TaskStatus;
  initialTitle?: string;
  initialDescription?: string;
  initialAssigneeId?: string;
  initialAttachments?: Array<{ fileName: string; fileSize: number; mimeType: string; url: string; fileId?: string }>;
  groupMembers?: User[];
}

const EMPTY_ATTACHMENTS: Array<{ fileName: string; fileSize: number; mimeType: string; url: string; fileId?: string }> = [];

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  onTaskCreated,
  projectId: defaultProjectId,
  initialStatus = 'todo',
  initialTitle = '',
  initialDescription = '',
  initialAssigneeId = '',
  initialAttachments = EMPTY_ATTACHMENTS,
  groupMembers,
}) => {
  const { token, user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<User[]>([]);

  const [projectId, setProjectId] = useState(defaultProjectId || '');
  const [builds, setBuilds] = useState<ProjectBuild[]>([]);
  const [features, setFeatures] = useState<ProjectFeature[]>([]);
  const [buildId, setBuildId] = useState<string>('');
  const [featureId, setFeatureId] = useState<string>('');
  const [isLoadingBuildsFeatures, setIsLoadingBuildsFeatures] = useState(false);

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [status, setStatus] = useState<TaskStatus>(initialStatus);
  const [priority, setPriority] = useState<PriorityLevel>('medium');
  const [assigneeId, setAssigneeId] = useState(initialAssigneeId || user?.id || '');
  const [projectMembersMap, setProjectMembersMap] = useState<Record<string, User[]>>({});
  const fetchedProjectsRef = React.useRef<Set<string>>(new Set());
  const wasOpenRef = React.useRef<boolean>(false);
  const [isViewingProjectMembers, setIsViewingProjectMembers] = useState(false);

  const getAuthHeaders = (extra: Record<string, string> = {}) => ({
    Authorization: `Bearer ${token || ''}`,
    ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
    ...extra,
  });

  // Dynamically load members of selected project if not already populated
  useEffect(() => {
    const pid = projectId || defaultProjectId;
    if (pid && !fetchedProjectsRef.current.has(pid) && token) {
      fetchedProjectsRef.current.add(pid);
      fetch(`/api/projects/${pid}`, { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => {
          if (d.project && Array.isArray(d.project.members) && d.project.members.length > 0) {
            setProjectMembersMap((prev) => ({ ...prev, [pid]: d.project.members }));
          }
        })
        .catch(() => {});
    }
  }, [projectId, defaultProjectId, token]);

  // Scope assignable members to groupMembers (when creating a task in a group/chat or board)
  // or to the selected project's attached members. If no project is selected, show all team members.
  const assignableMembers = React.useMemo(() => {
    let baseList: User[] = members;
    if (Array.isArray(groupMembers) && groupMembers.length > 0) {
      baseList = groupMembers;
    } else {
      const pid = projectId || defaultProjectId;
      if (pid) {
        if (projectMembersMap[pid] && projectMembersMap[pid].length > 0) {
          baseList = projectMembersMap[pid];
        } else {
          const selectedProj = projects.find((p) => p.id === pid);
          if (selectedProj && Array.isArray(selectedProj.members) && selectedProj.members.length > 0) {
            baseList = selectedProj.members;
          }
        }
      }
    }

    const seen = new Set<string>();
    const deduped = baseList.filter((m) => {
      if (!m || !m.id || seen.has(m.id)) return false;
      seen.add(m.id);
      return true;
    });

    // Always ensure the currently selected assignee (or current user) is selectable in the list
    if (assigneeId && !seen.has(assigneeId)) {
      const found = members.find((m) => m.id === assigneeId) || (user?.id === assigneeId ? user : null);
      if (found) {
        seen.add(found.id);
        deduped.push(found);
      }
    }
    if (user?.id && !seen.has(user.id)) {
      seen.add(user.id);
      deduped.push(user);
    }

    return deduped;
  }, [groupMembers, projects, projectId, defaultProjectId, projectMembersMap, members, assigneeId, user]);

  const currentSelectedProject = React.useMemo(() => {
    const pid = projectId || defaultProjectId;
    return projects.find((p) => p.id === pid) || null;
  }, [projects, projectId, defaultProjectId]);
  const [dueDate, setDueDate] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Quick project creation state
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectKey, setNewProjectKey] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [projectSuccessMsg, setProjectSuccessMsg] = useState<string | null>(null);

  const [taskAttachments, setTaskAttachments] = useState<any[]>(initialAttachments || EMPTY_ATTACHMENTS);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [previewFile, setPreviewFile] = useState<any | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const generateKeyFromName = (name: string) => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length > 1) {
      return words.map((w) => w[0]).join('').toUpperCase().slice(0, 4);
    }
    return name.replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
  };

  const handleNewProjectNameChange = (val: string) => {
    setNewProjectName(val);
    if (!newProjectKey || newProjectKey === generateKeyFromName(newProjectName)) {
      setNewProjectKey(generateKeyFromName(val));
    }
  };

  const uploadFiles = async (files: FileList | File[]) => {
    if (!token || files.length === 0) return;
    setIsUploadingAttachment(true);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      if (projectId) formData.append('projectId', projectId);

      try {
        const res = await fetch('/api/files/upload', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        });

        if (res.ok) {
          const data = await res.json();
          setTaskAttachments((prev) => [
            ...prev,
            {
              fileId: data.file.id,
              fileName: data.file.originalName,
              fileSize: data.file.fileSize,
              mimeType: data.file.mimeType,
              url: data.file.downloadUrl,
            },
          ]);
        } else {
          alert(await describeUploadFailure(res, file.name));
        }
      } catch (err: any) {
        console.error('Failed to upload attachment:', err);
        alert(`Could not upload "${file.name}": ${err.message || 'network error'}`);
      }
    }
    setIsUploadingAttachment(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  // Pasting a screenshot or copied image (Ctrl+V) anywhere in the open form attaches it,
  // exactly like dropping it on the attachments area. Pasted text is left alone.
  const uploadFilesRef = React.useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e: ClipboardEvent) => {
      const pasted = Array.from(e.clipboardData?.items || [])
        .filter((item) => item.kind === 'file')
        .map((item) => item.getAsFile())
        .filter((f): f is File => Boolean(f));
      // Copied text (even with a picture alongside, e.g. from a spreadsheet) pastes as text as usual
      if (pasted.length === 0 || e.clipboardData?.getData('text/plain')) return;
      e.preventDefault();
      const named = pasted.map((f) =>
        f.name && f.name !== 'image.png'
          ? f
          : new File([f], `pasted-image-${Date.now()}.${(f.type.split('/')[1] || 'png').replace('jpeg', 'jpg')}`, { type: f.type })
      );
      uploadFilesRef.current(named);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) {
      return;
    }
    if (isOpen && token) {
      wasOpenRef.current = true;
      setGeneralError(null);
      setProjectError(null);
      setProjectSuccessMsg(null);
      setTaskAttachments(
        Array.isArray(initialAttachments) && initialAttachments.length > 0
          ? [...initialAttachments]
          : []
      );

      if (defaultProjectId) {
        setProjectId(defaultProjectId);
        setIsCreatingProject(false);
      }

      fetch('/api/projects?status=active&allSpaces=true', { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => {
          const list = d.projects || [];
          setProjects(list);
          if (defaultProjectId) {
            setProjectId(defaultProjectId);
            setIsCreatingProject(false);
          } else if (list.length > 0) {
            setProjectId((prev) => {
              if (prev && list.some((p: any) => p.id === prev)) return prev;
              return list[0].id;
            });
            setIsCreatingProject(false);
          } else {
            setIsCreatingProject(true);
          }
        })
        .catch((err) => console.error('Failed to load projects:', err));

      fetch('/api/team/members', { headers: getAuthHeaders() })
        .then((r) => r.json())
        .then((d) => setMembers(d.members || []))
        .catch((err) => console.error('Failed to load members:', err));

      setStatus(initialStatus);
      if (initialTitle) setTitle(initialTitle);
      if (initialDescription) setDescription(initialDescription);
      if (initialAssigneeId) {
        setAssigneeId(initialAssigneeId);
      } else if (user?.id) {
        setAssigneeId(user.id);
      }
    }
  }, [isOpen, defaultProjectId, initialStatus, initialTitle, initialDescription, initialAssigneeId, initialAttachments, token, user?.id]);

  // Fetch Builds and Features when project is selected
  useEffect(() => {
    if (isOpen && projectId && token) {
      setIsLoadingBuildsFeatures(true);
      Promise.all([
        fetch(`/api/projects/${projectId}/builds`, { headers: getAuthHeaders() }).then((r) => r.json()),
        fetch(`/api/projects/${projectId}/features`, { headers: getAuthHeaders() }).then((r) => r.json()),
      ])
        .then(([bData, fData]) => {
          const bList: ProjectBuild[] = bData.builds || [];
          const fList: ProjectFeature[] = fData.features || [];
          setBuilds(bList);
          setFeatures(fList);
          if (bList.length > 0) {
            setBuildId(bList[0].id);
          } else {
            setBuildId('');
          }
          if (fList.length > 0) {
            setFeatureId(fList[0].id);
          } else {
            setFeatureId('');
          }
        })
        .catch((err) => console.error('Failed to load builds/features for project:', err))
        .finally(() => setIsLoadingBuildsFeatures(false));
    } else if (!projectId) {
      setBuilds([]);
      setFeatures([]);
      setBuildId('');
      setFeatureId('');
    }
  }, [isOpen, projectId, token]);

  const handleQuickCreateProject = async () => {
    if (!newProjectName.trim() || !token) return;
    setIsSubmittingProject(true);
    setProjectError(null);

    try {
      const keyToUse = (newProjectKey.trim() || generateKeyFromName(newProjectName)).toUpperCase();
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          name: newProjectName.trim(),
          key: keyToUse,
          description: newProjectDescription.trim(),
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to create project');
      }

      if (data.project) {
        setProjects((prev) => [data.project, ...prev]);
        setProjectId(data.project.id);
        setIsCreatingProject(false);
        setNewProjectName('');
        setNewProjectKey('');
        setNewProjectDescription('');
        setProjectSuccessMsg(`Created and selected: [${data.project.key}] ${data.project.name}`);
        window.dispatchEvent(new CustomEvent('project-created', { detail: data.project }));
      }
    } catch (err: any) {
      setProjectError(err.message || 'Failed to create project');
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError(null);

    let effectiveProjectId = projectId || defaultProjectId || '';

    // If user typed a project name in the inline creator but hit "Create Task" directly
    if (isCreatingProject && newProjectName.trim() && !effectiveProjectId) {
      setIsSubmitting(true);
      try {
        const keyToUse = (newProjectKey.trim() || generateKeyFromName(newProjectName)).toUpperCase();
        const projRes = await fetch('/api/projects', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token || ''}`,
            ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
          },
          body: JSON.stringify({
            name: newProjectName.trim(),
            key: keyToUse,
            description: newProjectDescription.trim(),
          }),
        });
        const projData = await projRes.json();
        if (!projRes.ok) {
          setProjectError(projData.error || 'Failed to create project');
          setIsSubmitting(false);
          return;
        }
        effectiveProjectId = projData.project.id;
        setProjects((prev) => [projData.project, ...prev]);
        setProjectId(effectiveProjectId);
        setIsCreatingProject(false);
        window.dispatchEvent(new CustomEvent('project-created', { detail: projData.project }));
      } catch (err: any) {
        setProjectError(err.message || 'Failed to create project');
        setIsSubmitting(false);
        return;
      }
    }

    if (!effectiveProjectId) {
      setGeneralError('Please select or create a project for this task.');
      return;
    }

    if (!title.trim() || !token) return;

    setIsSubmitting(true);
    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || ''}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          projectId: effectiveProjectId,
          buildId: buildId || null,
          featureId: featureId || null,
          title: title.trim(),
          description: description.trim(),
          status,
          priority,
          assigneeId: assigneeId || null,
          assignorId: user?.id || null,
          dueDate: dueDate || null,
          tags,
          attachments: taskAttachments,
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setTitle('');
        setDescription('');
        setTagsInput('');
        setDueDate('');
        setBuildId('');
        setFeatureId('');
        setTaskAttachments([]);
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        onTaskCreated(data.task);
        onClose();
      } else {
        const errData = await res.json().catch(() => ({}));
        setGeneralError(errData.error || 'Failed to create task');
      }
    } catch (err: any) {
      console.error('Failed to create task:', err);
      setGeneralError(err.message || 'Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Task" maxWidth="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Project Picker */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300">
              Project <span className="text-rose-500">*</span>
            </label>
            <div className="flex items-center space-x-2">
              {(projectId || defaultProjectId) && (
                <button
                  type="button"
                  onClick={() => setIsViewingProjectMembers(true)}
                  className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold text-brand-600 dark:text-brand-400 bg-brand-50 hover:bg-brand-100 dark:bg-brand-950/40 dark:hover:bg-brand-900/50 border border-brand-200/60 dark:border-brand-800/60 transition-colors"
                  title="Show existing members of this project"
                >
                  <Users className="w-3 h-3" />
                  <span>Existing Members ({assignableMembers.length})</span>
                </button>
              )}
              {!isCreatingProject && canCreateProject(user?.role) && (
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingProject(true);
                    setProjectError(null);
                  }}
                  className="inline-flex items-center space-x-1 text-[11px] font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Project</span>
                </button>
              )}
            </div>
          </div>

          {projectSuccessMsg && (
            <div className="flex items-center space-x-1.5 p-2 text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-xl animate-in fade-in">
              <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{projectSuccessMsg}</span>
            </div>
          )}

          {isCreatingProject ? (
            <div className="p-3.5 bg-brand-50/40 dark:bg-brand-950/20 border border-brand-200 dark:border-brand-800/60 rounded-xl space-y-3 animate-in fade-in">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5 text-xs font-semibold text-brand-700 dark:text-brand-300">
                  <FolderPlus className="w-3.5 h-3.5" />
                  <span>Create New Project</span>
                </div>
                {projects.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreatingProject(false);
                      setProjectError(null);
                    }}
                    className="text-[11px] text-surface-500 hover:text-surface-700 dark:hover:text-surface-300 flex items-center space-x-1"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Select existing instead</span>
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-medium text-surface-600 dark:text-surface-400 mb-0.5">
                    Project Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Mobile App Redesign"
                    value={newProjectName}
                    onChange={(e) => handleNewProjectNameChange(e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs rounded-lg bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-surface-600 dark:text-surface-400 mb-0.5">
                    Key Code <span className="text-surface-400 font-normal">(auto)</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. MAR"
                    maxLength={6}
                    value={newProjectKey}
                    onChange={(e) => setNewProjectKey(e.target.value.toUpperCase())}
                    className="w-full px-2.5 py-1.5 text-xs uppercase font-mono rounded-lg bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 placeholder-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
              </div>

              {projectError && (
                <p className="text-[11px] text-rose-500">{projectError}</p>
              )}

              <div className="flex items-center justify-between pt-1">
                <span className="text-[11px] text-surface-400">
                  Will be created and auto-selected for this task.
                </span>
                <button
                  type="button"
                  onClick={handleQuickCreateProject}
                  disabled={isSubmittingProject || !newProjectName.trim()}
                  className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                >
                  {isSubmittingProject ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5" />
                      <span>Save & Select Project</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <select
                  value={projectId}
                  onChange={(e) => {
                    if (e.target.value === '__NEW_PROJECT__') {
                      setIsCreatingProject(true);
                    } else {
                      setProjectId(e.target.value);
                    }
                  }}
                  required
                  className="flex-1 px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none focus:ring-1 focus:ring-brand-500 min-h-[40px]"
                >
                  <option value="" disabled>
                    {projects.length === 0 ? 'No projects available (click New Project)' : 'Select a project...'}
                  </option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      [{p.key}] {p.name}
                    </option>
                  ))}
                  {canCreateProject(user?.role) && <option value="__NEW_PROJECT__">＋ Add New Project...</option>}
                </select>

                {(projectId || defaultProjectId) && (
                  <button
                    type="button"
                    onClick={() => setIsViewingProjectMembers(true)}
                    className="px-2.5 py-2 rounded-xl bg-surface-100 hover:bg-surface-200 dark:bg-surface-800 dark:hover:bg-surface-700 text-surface-700 dark:text-surface-300 border border-surface-200 dark:border-surface-700 transition-colors shrink-0 flex items-center space-x-1.5 min-h-[40px]"
                    title={`Show existing members of ${currentSelectedProject?.name || 'this project'}`}
                  >
                    <Users className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
                    <span className="text-xs font-semibold">Members ({assignableMembers.length})</span>
                  </button>
                )}
              </div>
              {projects.length === 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400">
                  No active projects yet.{' '}
                  <button
                    type="button"
                    onClick={() => setIsCreatingProject(true)}
                    className="font-semibold underline hover:text-amber-700"
                  >
                    Click here to create your first project.
                  </button>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Task Title */}
        <div>
          <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
            Task Title <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            required
            autoFocus
            placeholder="e.g. Implement OAuth2 Refresh Token Rotation"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
            Description
          </label>
          <DescriptionEditor
            rows={3}
            placeholder="Context, requirements, acceptance criteria..."
            value={description}
            onChange={setDescription}
            className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
          />
        </div>

        {/* Status & Priority Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as TaskStatus)}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px]"
            >
              <option value="backlog">Backlog</option>
              <option value="todo">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="in_review">In Review</option>
              <option value="done">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as PriorityLevel)}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px]"
            >
              <option value="urgent">⚡ Urgent</option>
              <option value="high">▲ High</option>
              <option value="medium">● Medium</option>
              <option value="low">▼ Low</option>
            </select>
          </div>
        </div>

        {/* Assignor & Assignee Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Assignor (Assigned By)
            </label>
            <div className="flex items-center space-x-2.5 px-3 py-2 rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 min-h-[42px]">
              <Avatar src={user?.avatarUrl} name={user?.fullName || 'Assignor'} size="xs" />
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-surface-900 dark:text-surface-100 truncate block">
                  {user?.fullName || 'You'} <span className="text-[10px] text-brand-600 dark:text-brand-400 font-medium">(You)</span>
                </span>
                <span className="text-[10px] text-surface-400 block">Task Assignor</span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
              Assignee (Assigned To)
            </label>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px]"
            >
              <option value="">Unassigned</option>
              {assignableMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.fullName} {m.id === user?.id ? '(You)' : ''} ({m.role || 'Member'})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Due Date Row */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-surface-700 dark:text-surface-300 flex items-center space-x-1">
              <Calendar className="w-3.5 h-3.5 text-surface-400" />
              <span>Due Date</span>
            </label>
            <div className="flex items-center space-x-1">
              <button
                type="button"
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setDueDate(today);
                }}
                className="text-[10px] text-brand-600 hover:text-brand-700 bg-brand-50 dark:bg-brand-950/40 px-1.5 py-0.5 rounded font-medium"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() + 1);
                  setDueDate(d.toISOString().split('T')[0]);
                }}
                className="text-[10px] text-brand-600 hover:text-brand-700 bg-brand-50 dark:bg-brand-950/40 px-1.5 py-0.5 rounded font-medium"
              >
                Tomorrow
              </button>
              <button
                type="button"
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() + 7);
                  setDueDate(d.toISOString().split('T')[0]);
                }}
                className="text-[10px] text-brand-600 hover:text-brand-700 bg-brand-50 dark:bg-brand-950/40 px-1.5 py-0.5 rounded font-medium"
              >
                +1 Wk
              </button>
              {dueDate && (
                <button
                  type="button"
                  onClick={() => setDueDate('')}
                  className="text-[10px] text-rose-500 hover:text-rose-600 px-1 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            onClick={(e) => {
              try {
                (e.currentTarget as any).showPicker?.();
              } catch {}
            }}
            className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none min-h-[42px] cursor-pointer"
          />
        </div>

        {/* Tags */}
        <div>
          <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300 mb-1">
            Tags (comma separated)
          </label>
          <input
            type="text"
            placeholder="Security, Backend, Sprint-4"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            className="w-full px-3 py-2 text-xs rounded-xl bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 text-surface-900 dark:text-surface-100 focus:outline-none"
          />
        </div>

        {/* Attachments Section with Drag & Drop */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-semibold text-surface-700 dark:text-surface-300">
              Attachments {taskAttachments.length > 0 && `(${taskAttachments.length})`}
            </label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-[11px] font-semibold text-brand-600 hover:text-brand-700 flex items-center space-x-1"
            >
              <Paperclip className="w-3 h-3" />
              <span>Browse File</span>
            </button>
          </div>

          <input
            type="file"
            multiple
            ref={fileInputRef}
            className="hidden"
            onChange={(e) => {
              if (e.target.files) uploadFiles(e.target.files);
            }}
          />

          {/* Drag & Drop Zone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDraggingOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDraggingOver(false);
            }}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-3 text-center cursor-pointer transition-all ${
              isDraggingOver
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/30 ring-2 ring-brand-500/20'
                : 'border-surface-200 dark:border-surface-700/80 bg-surface-50/60 dark:bg-surface-850 hover:border-brand-400'
            }`}
          >
            <div className="flex flex-col items-center justify-center space-y-1">
              <UploadCloud
                className={`w-5 h-5 ${
                  isDraggingOver ? 'text-brand-600' : 'text-surface-400 dark:text-surface-500'
                }`}
              />
              <span className="text-xs font-semibold text-surface-700 dark:text-surface-300">
                {isDraggingOver ? 'Drop files here to attach' : 'Drag & drop files here, or click to upload'}
              </span>
              <span className="text-[10px] text-surface-400">
                Supports all files (PDF, docs, images, spreadsheets)
              </span>
            </div>
          </div>

          {/* List of Attached Files */}
          {taskAttachments.length > 0 && (
            <div className="space-y-1.5 pt-1">
              {taskAttachments.map((att, idx) => (
                <div
                  key={idx}
                  onClick={() => setPreviewFile(att)}
                  className="flex items-center justify-between p-2 rounded-xl bg-surface-100 dark:bg-surface-800/80 border border-surface-200 dark:border-surface-700 text-xs hover:border-brand-300 dark:hover:border-brand-700 cursor-pointer group transition-colors"
                >
                  <div className="flex items-center space-x-2 truncate pr-2">
                    <FileText className="w-3.5 h-3.5 text-brand-500 shrink-0 group-hover:scale-110 transition-transform" />
                    <span className="font-medium truncate text-surface-800 dark:text-surface-200 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                      {att.fileName}
                    </span>
                    <span className="text-[10px] text-surface-400 shrink-0">
                      ({(att.fileSize / 1024).toFixed(0)} KB)
                    </span>
                  </div>
                  <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => setPreviewFile(att)}
                      className="p-1 text-surface-400 hover:text-brand-600 rounded-lg hover:bg-brand-50 dark:hover:bg-brand-950/20 transition-colors"
                      title="Preview Document"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTaskAttachments((prev) => prev.filter((_, i) => i !== idx));
                      }}
                      className="p-1 text-surface-400 hover:text-rose-500 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors"
                      title="Remove attachment"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {generalError && (
          <p className="text-xs text-rose-500 font-medium">{generalError}</p>
        )}

        {/* Actions */}
        <div className="pt-4 border-t border-surface-100 dark:border-surface-800 flex justify-end space-x-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !title.trim()}
            className="inline-flex items-center space-x-1.5 px-5 py-2 text-xs font-semibold bg-brand-600 hover:bg-brand-700 text-white rounded-xl shadow-sm disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Creating Task...</span>
              </>
            ) : (
              <span>Create Task</span>
            )}
          </button>
        </div>
      </form>

      {/* Interactive Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={Boolean(previewFile)}
        onClose={() => setPreviewFile(null)}
        file={previewFile}
      />

      {/* Existing Project Members Modal */}
      {isViewingProjectMembers && (
        <ExistingProjectMembersModal
          isOpen={isViewingProjectMembers}
          onClose={() => setIsViewingProjectMembers(false)}
          projectName={currentSelectedProject?.name || 'Selected Project'}
          members={assignableMembers}
        />
      )}
    </Modal>
  );
};
